import moment from 'moment';

import type { IInvoiceAggregateRow as IFakturowniaInvoiceAggregateRow } from 'core/hooks/Fakturownia/useFakturowniaInvoiceAggregates';

/**
 * Per-currency money model for the GroupDetail FinancesTab (design Money_Model:
 * Planned → Issued → Paid → Debt).
 *
 * `buildMoneyModel` is a pure projection over the contract side (contributions
 * of the group's active agreements) joined with the invoice side (per-currency
 * Fakturownia aggregates). It produces exactly one row per currency that
 * appears in an **active** (non-terminated) agreement and NEVER sums or borrows
 * figures across currencies — a multi-currency group is reported per currency.
 *
 * The contract-side aggregation mirrors the walk in
 * `parts/ContributionsSummary.tsx` exactly — it iterates
 * `agreement.contributions.edges[].node` of the active agreements:
 *   - planned       — Σ contribution `amountForeign`
 *   - paid          — Σ `amountForeign` of completed contributions
 *   - debt          — max(0, planned − paid)
 *   - overdueCount  — `!complete && date < today`
 *   - upcomingCount — `!complete && date >= today` (incl. undated incomplete)
 *
 * The invoice side comes from `invoiceAggByCurrency`: per-currency Fakturownia
 * aggregate rows (the shape produced by `useFakturowniaInvoiceAggregates` with
 * `groupBy: 'currency'`, where the `group` bucket key is the currency code and
 * `totalGross` is the issued gross). A currency's `issued` is that same
 * currency's invoice gross, or `null` with `hasInvoiceData = false` when the
 * invoice side has no row for it.
 *
 * The function is intentionally decoupled from React/Apollo (no hooks, no
 * generated query value imports) so it can be exercised cheaply by the
 * property-based tests in isolation. The input interfaces are minimal/
 * structural so callers can pass their query nodes directly — the real
 * Fakturownia `IInvoiceAggregateRow` is assignable to {@link IInvoiceAggregateRow}
 * (verified by {@link InvoiceAggregateRowContract} below).
 */

/** A single contribution node from an agreement's payment schedule. */
export interface IGroupFinanceContributionNode {
  /** Contract-side amount in the agreement's own currency. */
  amountForeign?: number | string | null;
  /** Whether this contribution has been paid in full. */
  complete?: boolean | null;
  /** Scheduled date (ISO string); undated incomplete rows count as upcoming. */
  date?: string | null;
}

/** A relay-style edge wrapping a contribution node. */
export interface IGroupFinanceContributionEdge {
  node?: IGroupFinanceContributionNode | null;
}

/** The contributions connection on an agreement (`contributions { edges { node } }`). */
export interface IGroupFinanceContributionConnection {
  edges?: ReadonlyArray<IGroupFinanceContributionEdge | null> | null;
}

/**
 * A group agreement reduced to what the money model needs — mirrors the
 * `group.agreements[]` shape walked by `ContributionsSummary`. Terminated
 * agreements are skipped (their schedule is closed by definition).
 */
export interface IGroupFinanceAgreement {
  /** The agreement's currency code (drives the per-currency bucket). */
  currency?: string | null;
  /** Terminated agreements are excluded from the model. */
  terminated?: boolean | null;
  /** The payment schedule (`contributions { edges { node } }`). */
  contributions?: IGroupFinanceContributionConnection | null;
}

/**
 * One per-currency invoice-side aggregate row. Structural subset of the
 * Fakturownia `IInvoiceAggregateRow` produced by `useFakturowniaInvoiceAggregates`
 * (`groupBy: 'currency'`): the `group` bucket key holds the currency code and
 * `totalGross` holds the issued gross for that currency.
 */
export interface IInvoiceAggregateRow {
  /** Bucket key — the currency code when aggregated by currency. */
  group?: string | null;
  /** Σ issued invoice gross for this currency. */
  totalGross?: number | string | null;
}

/**
 * Compile-time helper that resolves to `TActual` only while it is assignable to
 * `TExpected`; otherwise the constraint fails and the alias stops type-checking.
 */
type AssertAssignable<TActual extends TExpected, TExpected> = TActual;

/**
 * Compile-time contract: the concrete Fakturownia aggregate row produced by
 * `useFakturowniaInvoiceAggregates` must stay assignable to the structural
 * {@link IInvoiceAggregateRow} this util consumes, so the FinancesTab can feed
 * the hook's rows straight into {@link buildMoneyModel}. If the Fakturownia row
 * drifts away from `group`/`totalGross`, this alias fails to compile here rather
 * than at the distant call site.
 */
export type InvoiceAggregateRowContract = AssertAssignable<
  IFakturowniaInvoiceAggregateRow,
  IInvoiceAggregateRow
>;

/** One row of the per-currency Money_Model. */
export interface ICurrencyMoneyModel {
  currency: string;
  /** Σ contribution `amountForeign` of active agreements — contract side. */
  planned: number;
  /** Σ invoice gross for this currency — invoice side; `null` when absent. */
  issued: number | null;
  /** Σ paid (completed contributions) for this currency — contract side. */
  paid: number;
  /** `max(0, planned − paid)`. */
  debt: number;
  /** Count of incomplete contributions whose date is before today. */
  overdueCount: number;
  /** Count of incomplete contributions due today or later (incl. undated). */
  upcomingCount: number;
  /** Whether the invoice side has data for this currency. */
  hasInvoiceData: boolean;
}

/** Defensive numeric coercion — Decimals may arrive as strings; bad input → 0. */
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

interface IMutableRow {
  currency: string;
  planned: number;
  paid: number;
  overdueCount: number;
  upcomingCount: number;
}

/**
 * Builds the per-currency Money_Model.
 *
 * @param contributions       The group's agreements (terminated ones ignored),
 *                            each carrying its `contributions` schedule — the
 *                            contract side of the money model.
 * @param invoiceAggByCurrency Per-currency Fakturownia invoice aggregates — the
 *                            invoice side.
 */
export const buildMoneyModel = (
  contributions: IGroupFinanceAgreement[],
  invoiceAggByCurrency: IInvoiceAggregateRow[],
): ICurrencyMoneyModel[] => {
  const today = moment().startOf('day');

  // Index the invoice side by currency so a currency only ever reads its own
  // gross — no borrowing across currencies.
  const invoiceByCurrency = new Map<string, number>();
  (invoiceAggByCurrency || []).forEach((agg) => {
    if (!agg) return;
    const currency = agg.group || '';
    invoiceByCurrency.set(currency, toNumber(agg.totalGross));
  });

  // Aggregate the contract side per currency, active agreements only.
  const rowByCurrency = new Map<string, IMutableRow>();
  (contributions || []).forEach((agreement) => {
    if (!agreement || agreement.terminated) return;
    const currency = agreement.currency || '';
    const row = rowByCurrency.get(currency) || {
      currency,
      planned: 0,
      paid: 0,
      overdueCount: 0,
      upcomingCount: 0,
    };

    (agreement.contributions?.edges || []).forEach((edge) => {
      const contribution = edge?.node;
      if (!contribution) return;
      const amount = toNumber(contribution.amountForeign);
      row.planned += amount;
      if (contribution.complete) {
        row.paid += amount;
      } else if (
        contribution.date &&
        moment(contribution.date).isBefore(today)
      ) {
        row.overdueCount += 1;
      } else {
        row.upcomingCount += 1;
      }
    });

    rowByCurrency.set(currency, row);
  });

  return Array.from(rowByCurrency.values())
    .map((row) => {
      const hasInvoiceData = invoiceByCurrency.has(row.currency);
      return {
        currency: row.currency,
        planned: row.planned,
        issued: hasInvoiceData
          ? (invoiceByCurrency.get(row.currency) as number)
          : null,
        paid: row.paid,
        debt: Math.max(0, row.planned - row.paid),
        overdueCount: row.overdueCount,
        upcomingCount: row.upcomingCount,
        hasInvoiceData,
      };
    })
    .sort((a, b) => b.planned - a.planned);
};
